import type { RevalidationFeedback } from "./CoreDemographicIhiRevalidation";

/** CU-14ygtkj98pb (HI_015_005815): bounded contract for the explicit operator address Search Type 6. Fails closed; nothing here logs. */

export const ADDRESS_IHI_SEARCH_REQUIREMENT_ID = "HI_015_005815";

// CU-14ygtkjb8x9 (HI_015_005875): present only on a search for a patient without a current IHI, which may assign one.
export const ADDRESS_IHI_ASSIGNMENT_REQUIREMENT_ID = "HI_015_005875";

const CURRENT_IHI_OUTCOMES = ["current_ihi_confirmed", "different_ihi_returned"] as const;

const SEARCH_OUTCOMES = ["no_match", "multiple_match", "search_rejected", "search_unavailable", "indeterminate"] as const;

const ASSIGNED_OUTCOMES = ["ihi_assigned", "ihi_assigned_and_flagged"] as const;

const UNASSIGNED_OUTCOMES = ["ihi_not_assigned", "ihi_assignment_superseded"] as const;

const OUTCOMES = [...CURRENT_IHI_OUTCOMES, ...SEARCH_OUTCOMES, ...ASSIGNED_OUTCOMES, ...UNASSIGNED_OUTCOMES] as const;

export type AddressIhiSearchOutcome = (typeof OUTCOMES)[number];

const NUMBER_STATUSES = ["active", "deceased", "retired", "expired", "resolved"] as const;
const RECORD_STATUSES = ["verified", "unverified", "provisional"] as const;

/** CU-14ygtkj98pb: a held current IHI, whatever its record status, keeps the address search on the revalidation path. */
export const hasServerConfirmedCurrentIhi = (ihi: { ihi_number?: unknown } | null | undefined): boolean => {
	const ihiNumber = ihi?.ihi_number;

	return (typeof ihiNumber === "number" && Number.isFinite(ihiNumber)) || (typeof ihiNumber === "string" && ihiNumber.trim() !== "");
};

export interface AddressIhiSearchResult {
	outcome: AddressIhiSearchOutcome;
	numberStatus: string | null;
	recordStatus: string | null;
	patientRecordChanged: boolean;
	noCurrentIhiAssignment: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const inList = <T extends string>(list: readonly T[], value: unknown): value is T =>
	(list as readonly unknown[]).includes(value);

const boundedLabel = (list: readonly string[], value: unknown): string | null | undefined => {
	if (value === null) return null;
	return inList(list, value) ? value : undefined;
};

export const parseAddressIhiSearchResult = (value: unknown): AddressIhiSearchResult | null => {
	if (!isRecord(value)) return null;
	if (value.requirement_id !== ADDRESS_IHI_SEARCH_REQUIREMENT_ID || value.search_type_number !== 6) return null;
	if (!inList(OUTCOMES, value.outcome) || typeof value.patient_record_changed !== "boolean") return null;

	const noCurrentIhiAssignment = value.assignment_requirement_id === ADDRESS_IHI_ASSIGNMENT_REQUIREMENT_ID;
	if (value.assignment_requirement_id !== undefined && !noCurrentIhiAssignment) return null;
	if (noCurrentIhiAssignment ? inList(CURRENT_IHI_OUTCOMES, value.outcome) : !inList([...CURRENT_IHI_OUTCOMES, ...SEARCH_OUTCOMES], value.outcome)) return null;
	if (noCurrentIhiAssignment && value.patient_record_changed !== inList(ASSIGNED_OUTCOMES, value.outcome)) return null;

	const numberStatus = boundedLabel(NUMBER_STATUSES, value.number_status);
	const recordStatus = boundedLabel(RECORD_STATUSES, value.record_status);
	if (numberStatus === undefined || recordStatus === undefined) return null;

	return { outcome: value.outcome, numberStatus, recordStatus, patientRecordChanged: value.patient_record_changed, noCurrentIhiAssignment };
};

const titleCase = (label: string): string => label.charAt(0).toUpperCase() + label.slice(1);

const statusSummary = (result: AddressIhiSearchResult): string => {
	const parts = [
		result.numberStatus ? `number status ${titleCase(result.numberStatus)}` : null,
		result.recordStatus ? `record status ${titleCase(result.recordStatus)}` : null,
	].filter(Boolean);
	return parts.length > 0 ? ` (${parts.join(", ")})` : "";
};

const UNCHANGED = "The stored IHI and its statuses have not been changed.";

const NOTHING_STORED = "No IHI has been stored.";

const describeAssignmentSearchResult = (result: AddressIhiSearchResult | null): RevalidationFeedback => {
	switch (result?.outcome) {
		case "ihi_assigned":
			return { severity: "success", title: "IHI assigned", detail: `The address search returned one verified IHI${statusSummary(result)}. It has been assigned to this patient's record.` };
		case "ihi_assigned_and_flagged":
			return { severity: "warning", title: "IHI assigned — records flagged", detail: `The address search returned one verified IHI${statusSummary(result)} and it has been assigned to this patient's record. Another patient record matches the same search details, so the records have been flagged as potentially conflicting. Review the alert in HI Reviews.` };
		case "ihi_not_assigned":
			return { severity: "warning", title: "IHI not assigned", detail: `The address search returned an IHI that cannot be assigned${statusSummary(result)}. ${NOTHING_STORED} Any alert raised is listed in HI Reviews.` };
		case "ihi_assignment_superseded":
			return { severity: "warning", title: "IHI not assigned — record changed", detail: `The patient's record changed while the address search was running, so the returned IHI was not assigned. ${NOTHING_STORED} Refresh the patient record before searching again.` };
		case "no_match":
			return { severity: "warning", title: "No matching IHI found", detail: `No IHI matched the patient's saved name, date of birth, sex and address. ${NOTHING_STORED} Check the saved details before searching again.` };
		case "multiple_match":
			return { severity: "warning", title: "IHI could not be confirmed", detail: `The address search did not return a single IHI. ${NOTHING_STORED}` };
		case "search_rejected":
			return { severity: "warning", title: "Address search not sent", detail: `The address search needs the patient's saved family name, given name, date of birth, sex and address. ${NOTHING_STORED}` };
		case "search_unavailable":
			return { severity: "error", title: "IHI address search unavailable", detail: `The HI Service could not be reached. ${NOTHING_STORED} Try again later.` };
		default:
			return { severity: "warning", title: "IHI could not be confirmed", detail: `The address search result could not be confirmed. ${NOTHING_STORED}` };
	}
};

export const describeAddressIhiSearchResult = (result: AddressIhiSearchResult | null, noCurrentIhi = false): RevalidationFeedback => {
	if (result ? result.noCurrentIhiAssignment : noCurrentIhi) return describeAssignmentSearchResult(result);

	switch (result?.outcome) {
		case "current_ihi_confirmed":
			return result.patientRecordChanged
				? { severity: "warning", title: "IHI status updated", detail: `The address search returned the same IHI${statusSummary(result)}. Its status was updated and a warning was raised in HI Reviews.` }
				: { severity: "success", title: "IHI confirmed by address search", detail: `The address search returned the same IHI${statusSummary(result)}. ${UNCHANGED}` };
		case "different_ihi_returned":
			return { severity: "warning", title: "A different IHI was returned", detail: `The address search returned a different IHI${statusSummary(result)}. ${UNCHANGED} Review the patient's identity before taking further action.` };
		case "no_match":
			return { severity: "warning", title: "No matching IHI found", detail: `No IHI matched the patient's saved name, date of birth, sex and address. ${UNCHANGED} A review is open in HI Reviews where an amended address search can be submitted.` };
		case "multiple_match":
			return { severity: "warning", title: "IHI could not be confirmed", detail: `The address search did not return a single IHI. ${UNCHANGED}` };
		case "search_rejected":
			return { severity: "warning", title: "Address search not sent", detail: `The address search needs the patient's saved family name, given name, date of birth, sex and address. ${UNCHANGED}` };
		case "search_unavailable":
			return { severity: "error", title: "IHI address search unavailable", detail: `The HI Service could not be reached. ${UNCHANGED} Try again later.` };
		default:
			return { severity: "warning", title: "IHI could not be confirmed", detail: `The address search result could not be confirmed. ${UNCHANGED}` };
	}
};

export const describeAddressIhiSearchError = (error: unknown): RevalidationFeedback => {
	const status = isRecord(error) && isRecord(error.response) ? error.response.status : undefined;

	if (status === 409) {
		return { severity: "warning", title: "Address search not available", detail: "An IHI address search is not available for this patient record, for example when the patient has one name only." };
	}

	return { severity: "error", title: "Address search could not be requested", detail: `${UNCHANGED} Try again later.` };
};
